import {
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
  TextInput,
} from '@tremor/react';
import { CandidateType } from '../../../../mirage/types';
import { CandidateList } from '../Candidates/CandidateList';
import { Pill } from 'evergreen-ui';
import { RiSearchLine } from '@remixicon/react';
import { useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { RespondedCandidateList } from '../Candidates/RespondedCandidates/RespondedCandidateList';
import { useQuery } from 'react-query';
import { getCandidates } from '../../../../network/ats';
import { useNavigate } from 'react-router-dom';
import { normalizeCandidates } from '../../utils/candidate';

export const FindCandidates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const { data: response = { data: [] } } = useQuery({
    queryKey: ['candidates', 'all'],
    queryFn: () => getCandidates(),
  });

  const { data: applied_response = { data: [] } } = useQuery({
    queryKey: ['candidates', 'applied'],
    queryFn: () => getCandidates({ status: 'applied' }),
  });

  const showUser = (candidate: CandidateType) => {
    navigate(`/ats/candidate/${encodeURIComponent(String(candidate.id))}`);
  };

  const allCandidates = useMemo(
    () => normalizeCandidates(response?.data || []),
    [response],
  );
  const appliedCandidates = useMemo(
    () => normalizeCandidates(applied_response?.data || []),
    [applied_response],
  );

  const fuse = useMemo(() => {
    if (allCandidates.length === 0) {
      return new Fuse([], { keys: [] });
    }
    return new Fuse(allCandidates, {
      keys: [
        'name',
        'skills',
        'position',
        'location',
        'description',
        'department',
        'status',
      ],
      threshold: 0.3,
    });
  }, [allCandidates]);

  const filteredCandidates: CandidateType[] = useMemo(() => {
    if (!searchTerm) {
      return allCandidates;
    }
    if (!fuse) return [];

    return fuse.search(searchTerm).map((result) => result.item);
  }, [searchTerm, fuse, allCandidates]);

  return (
    <>
      <div className="rounded-xl bg-white shadow">
        <div className="flex flex-row items-center justify-between px-6 py-5">
          <div>
            <div className="text-lg font-semibold text-gray-900">Candidate</div>
            <div className="text-sm font-normal text-gray-600">
              Candidates who responded to your vacancies
            </div>
          </div>

          <div className="flex flex-row gap-4">
            <TextInput
              icon={RiSearchLine}
              placeholder="Search All Candidates..."
              className="w-auto lg:w-[400px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="h-[1px] !min-w-full bg-gray-300 p-0"></div>
        <div>
          <TabGroup>
            <TabList variant="line" color="red-500" className="m-6">
              <Tab>All Candidates</Tab>
              <Tab>
                <div className="flex flex-row items-center gap-1">
                  <div>Candidates who responded</div>{' '}
                  <Pill className="text-xs font-medium" color="green">
                    {appliedCandidates.length}
                  </Pill>
                </div>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <CandidateList showUser={showUser} candidates={filteredCandidates} />
              </TabPanel>
              <TabPanel>
                <RespondedCandidateList
                  showUser={showUser}
                  candidates={appliedCandidates}
                />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </div>
    </>
  );
};
